/**
 * Translator class handles dynamic translation of text content on the page
 * using Google Translate API. It supports both static and dynamic content,
 * including form fields, error messages, and data table filters.
 * 
 * The class implements a caching mechanism to store translations locally
 * and handles DOM mutations to translate dynamically added content.
 * 
 * @class
 * @property {string} apiKey - Google Translate API key for authentication
 * @property {Object} cachedTranslations - Cache of previously translated texts, stored by language code
 * @property {Object} originalEnTexts - Store of original English texts for restoration
 * @property {string} excludeSelectors - CSS selectors for elements to exclude from translation
 * @property {string[]} translatableAttributes - HTML attributes that should be translated (title, placeholder, alt)
 * @property {boolean} isObserving - Flag indicating if mutation observer is active
 * @property {MutationObserver} observer - Observer for DOM changes to handle dynamic content
 * @property {number} debounceTimeout - Timeout ID for debouncing translations
 */
export default class Translator {
    /**
     * Creates a new Translator instance
     * @param {string} apiKey - Google Translate API key for authentication
     * @throws {Error} If apiKey is not provided
     */
    constructor(apiKey) {
        if (!apiKey) {
            throw new Error('API key is required for Translator initialization');
        }
        this.apiKey = apiKey;
        this.cachedTranslations = JSON.parse(localStorage.getItem('translations')) || {};
        this.originalEnTexts = JSON.parse(localStorage.getItem('originalEnTexts')) || {};
        this.excludeSelectors = 'script, style, noscript, iframe, svg, input[type="hidden"], code, pre, .no-translate, .v-select, .vs__dropdown-menu, .vgt-table tbody, .footer__navigation__info';
        this.translatableAttributes = ['title', 'placeholder', 'alt'];

        this.isObserving = false;
        this.observer = new MutationObserver(this.handleMutations.bind(this));
        this.debounceTimeout;
    }

    /**
     * Starts observing DOM changes for dynamic content translation
     * This method sets up a MutationObserver to watch for changes in the DOM
     * and trigger translations when new content is added or modified.
     * 
     * @returns {void}
     */
    startObserving() {
        if (!this.isObserving) {
            this.observer.observe(document.body, {
                childList: true,
                subtree: true,
                characterData: true,
                characterDataOldValue: true
            });
            this.isObserving = true;
        }
    }

    /**
     * Stops observing DOM changes
     * This method disconnects the MutationObserver to stop watching for DOM changes.
     * 
     * @returns {void}
     */
    stopObserving() {
        if (this.isObserving) {
            this.observer.disconnect();
            this.isObserving = false;
        }
    }

    /**
     * Handles DOM mutations and triggers translation of new or changed content
     * This method processes DOM mutations and determines which elements need translation.
     * It uses debouncing to prevent excessive API calls.
     * 
     * @param {MutationRecord[]} mutationsList - List of DOM mutations to process
     * @returns {void}
     */
    handleMutations(mutationsList) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => {
            const selectedLang = this.getSelectedLanguage();
            if (selectedLang === 'en') return;
            // Filter out mutations caused by our own translation process
            const relevantMutations = mutationsList.filter(mutation => {
                // Skip mutations on elements with data-translate-id attribute that we're actively translating
                if (mutation.target && mutation.target.nodeType === 1) {
                    const el = mutation.target;
                    if (el.hasAttribute && (
                        el.hasAttribute('data-translate-id') ||
                        el.hasAttribute('data-translate-id-title') ||
                        el.hasAttribute('data-translate-id-placeholder') ||
                        el.hasAttribute('data-translate-id-alt')
                    )) {
                        return false;
                    }
                }

                // For text nodes, check if parent has translation attributes
                if (mutation.target && mutation.target.nodeType === 3 && mutation.target.parentNode) {
                    const parent = mutation.target.parentNode;
                    if (
                        parent.hasAttribute && (
                            parent.hasAttribute('data-translate-id') ||
                            parent.hasAttribute('data-translate-id-title') ||
                            parent.hasAttribute('data-translate-id-placeholder') ||
                            parent.hasAttribute('data-translate-id-alt')
                        )
                    ) {
                        return false;
                    }
                }

                return true;
            });

            if (relevantMutations.length === 0) return;

            const elementsToTranslate = new Set();

            for (let i = 0; i < relevantMutations.length; i++) {
                const mutation = relevantMutations[i];

                // Handle added nodes
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((addedNode) => {
                        if (addedNode.nodeType === 1) {
                            const selectedTags = addedNode.matches('.selected-tag')
                                ? [addedNode]
                                : Array.from(addedNode.querySelectorAll('.selected-tag'));

                            selectedTags.forEach(tag => {
                                const insideConfig = tag.closest('#dataTableFilterConfig');
                                const isExcluded = tag.closest('.no-translate');
                                if (insideConfig && !isExcluded) {
                                    elementsToTranslate.add(tag);
                                }
                            });

                            elementsToTranslate.add(addedNode);
                        }
                    });

                    // Check for removed .selected-tag (indicates placeholder might be visible again)
                    mutation.removedNodes.forEach((removedNode) => {
                        if (removedNode.nodeType === 1 && removedNode.classList.contains('selected-tag')) {
                            const vSelect = mutation.target.closest('.v-select');
                            if (vSelect) {
                                const input = vSelect.querySelector('input[placeholder]');
                                if (input) {
                                    let nodeId = input.getAttribute(`data-translate-id-placeholder`);
                                    console.log(nodeId);

                                    if (nodeId) {
                                        if (this.cachedTranslations[selectedLang] && this.cachedTranslations[selectedLang][nodeId]) {
                                            input.setAttribute('placeholder', this.cachedTranslations[selectedLang][nodeId]);
                                        }
                                    } else {
                                        elementsToTranslate.add(input);
                                    }
                                }
                            }
                        }
                    });

                }

                // Handle text content changes
                if (mutation.type === 'characterData') {
                    const textNode = mutation.target;
                    const parent = textNode.parentNode;

                    if (parent && parent instanceof Element &&
                        (parent.closest('#dataTableFilterConfig') || parent.classList.contains('field-error')) &&
                        !parent.closest('.no-translate')) {

                        const selectedTag = parent.closest('.selected-tag');
                        if (selectedTag) {
                            elementsToTranslate.add(selectedTag);
                        } else {
                            elementsToTranslate.add(parent);
                        }
                    }
                }

                // Handle placeholder attribute changes
                if (
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'placeholder'
                ) {
                    const target = mutation.target;
                    if (
                        target instanceof Element
                    ) {
                        elementsToTranslate.add(target);
                    }
                }
            }

            if (elementsToTranslate.size > 0) {
                this.translateElements(Array.from(elementsToTranslate), selectedLang);
            }
        }, 100);
    }

    /**
     * Translates a collection of DOM elements
     * This method processes a set of elements and translates their content
     * using the Google Translate API.
     * 
     * @param {Element[]} elements - Array of elements to translate
     * @param {string} lang - Target language code (e.g., 'es', 'fr')
     * @returns {Promise<void>}
     */
    async translateElements(elements, lang) {
        if (lang === 'en') return;

        const textsToTranslate = [];

        // Collect texts from each element
        elements.forEach(element => {
            const texts = this._collectTextsInElement(element, lang);
            textsToTranslate.push(...texts);
        });

        // Only proceed if we have valid English texts to translate
        const validTexts = textsToTranslate.filter(item =>
            /^[a-zA-Z0-9\s.,!?\/()\-:%&#]+$/.test(item.text)
        );

        if (validTexts.length > 0) {
            localStorage.setItem('originalEnTexts', JSON.stringify(this.originalEnTexts));
            await this._translateAndReplace(validTexts, lang);
        }
    }

    /**
     * Translates the entire page content
     * This method translates all translatable content on the page
     * and sets up observation for dynamic content.
     * 
     * @returns {Promise<void>}
     */
    async translatePage() {
        const lang = this.getSelectedLanguage();
        await this.restoreOriginalText();
        if (lang === 'en') return;

        const textsToTranslate = await this.collectTextsForTranslation(lang);
        // Only proceed if we have valid English texts to translate
        const validTexts = textsToTranslate.filter(item =>
            /^[a-zA-Z0-9\s.,!?\/()\-:%&#]+$/.test(item.text)
        );
        if (validTexts.length > 0) {
            await this._translateAndReplace(validTexts, lang);
        }
        if (!this.isObserving) {
            this.startObserving();
        }
    }

    /**
     * Collects all translatable texts from the page
     * This method traverses the DOM to find all text content that needs translation.
     * 
     * @param {string} lang - Target language code
     * @param {Element} [root=document.body] - Root element to start collection from
     * @returns {Promise<Object[]>} Array of text objects to translate
     */
    async collectTextsForTranslation(lang, root = document.body) {
        const textsToTranslate = this._collectTextsInElement(root, lang);
        localStorage.setItem('originalEnTexts', JSON.stringify(this.originalEnTexts));
        return textsToTranslate;
    }

    /**
     * Collects all translatable texts and attributes within a given DOM element.
     * This method traverses the DOM tree, identifies text nodes and specific attributes
     * that need translation, and prepares them for translation processing.
     * 
     * Special handling is applied for dynamic components (e.g., `.v-select`, `.custome-checkbox`),
     * placeholders, and elements with specific exclusion rules.
     * 
     * @param {HTMLElement} element - The root DOM element to search for translatable content.
     * @param {string} lang - The target language code for translation.
     * @returns {Array<Object>} An array of objects representing texts and attributes to translate.
     * Each object contains the following properties:
     *   - {string} id - A unique identifier for the text or attribute.
     *   - {string} text - The original text or attribute value to translate.
     *   - {string} [attr] - The attribute name (if applicable).
     *   - {HTMLElement} [element] - The DOM element associated with the attribute (if applicable).
     *   - {string} [flag] - A flag indicating special handling (e.g., 'remove' for placeholders).
     */
    _collectTextsInElement(element, lang) {
        const textsToTranslate = [];

        // Special handling for v-select components
        const vSelectComponents = element.querySelectorAll('.v-select, [role="listbox"]');
        vSelectComponents.forEach(select => {
            // Mark v-select components with a special attribute to identify them
            select.setAttribute('data-dynamic-translate', 'true');
        });

        // Special handling for checkboxs components
        const checkboxs = element.querySelectorAll('.custome-checkbox');
        checkboxs.forEach(check => {
            // Mark checkbox components with a special attribute to identify them
            check.setAttribute('data-dynamic-translate', 'true');
        });


        const walker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    const text = node.nodeValue.trim();
                    if (!text) return NodeFilter.FILTER_REJECT;

                    const parent = node.parentNode;
                    if (!parent) return NodeFilter.FILTER_REJECT;

                    // Reject if the text is just a 4-digit year (from 1000 to 2099)
                    if (/^(19|20)\d{2}$/.test(text)) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    const isExcluded = parent.closest(this.excludeSelectors);
                    const isInsideException = parent.closest('#dataTableFilterConfig');
                    const isDatetimeWidget = parent.closest('.bootstrap-datetimepicker-widget');
                    const isDateRangePicker = parent.closest('.mdrp__panel');

                    if ((isExcluded && !isInsideException) || isDatetimeWidget || isDateRangePicker) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    // Reject if inside #dataTableFilterConfig and also has .no-translate parent
                    if (isInsideException && parent.closest('.no-translate')) {
                        return NodeFilter.FILTER_REJECT;
                    }

                    return NodeFilter.FILTER_ACCEPT;
                }
            },
            false
        );

        this.translatableAttributes.forEach(attr => {
            const elements = element.querySelectorAll(`[${attr}]`);
            elements.forEach(el => {
                const value = el.getAttribute(attr);
                if (!value) return;

                // Track if the placeholder should be removed
                let flag = '';

                // Skip translating placeholder if inside .v-select and has a selected-tag
                if (attr === 'placeholder') {
                    const vSelect = el.closest('.v-select');
                    if (vSelect) {
                        const hasSelectedTag = vSelect.querySelector('.selected-tag');
                        // If a selected-tag exists, remove the placeholder and flag it
                        if (hasSelectedTag) {
                            el.removeAttribute('placeholder'); // Remove placeholder if a selection exists
                            flag = 'remove';
                        }
                    }
                }

                // Generate or retrieve the unique nodeId
                let nodeId = el.getAttribute(`data-translate-id-${attr}`);
                if (!nodeId) {
                    nodeId = this._generateStableNodeId(el, null) + `-${attr}`;
                    el.setAttribute(`data-translate-id-${attr}`, nodeId);
                }

                const originalAttrValue = this.originalEnTexts[nodeId] || value;
                if (!this.originalEnTexts[nodeId]) {
                    if (/^[a-zA-Z0-9\s.,!?\/()\-:%&#]+$/.test(value)) {
                        this.originalEnTexts[nodeId] = value;
                    }
                }

                // Check if cached translation exists for the language and nodeId
                if (this.cachedTranslations[lang] && this.cachedTranslations[lang][nodeId]) {
                    el.setAttribute(attr, this.cachedTranslations[lang][nodeId]);
                    // If placeholder was removed, ensure it's not added back
                    if (flag === 'remove') {
                        el.removeAttribute(attr);
                    }
                } else {
                    // If no cached translation, add the text for translation
                    const translationData = { id: nodeId, text: originalAttrValue, attr, element: el };
                    if (flag === 'remove') {
                        translationData.flag = 'remove';
                    }
                    textsToTranslate.push(translationData);
                }
            });
        });

        while (walker.nextNode()) {
            const textNode = walker.currentNode;
            const rawText = textNode.nodeValue.trim();
            const parent = textNode.parentNode;

            if (!rawText || rawText === 'Wait.. While we are collecting data for you...') continue;

            // Check if this text node is part of a dynamic component like v-select
            const isDynamicComponent = parent.closest('[data-dynamic-translate="true"]');
            // For dynamic components, we generate a more specific ID that includes the text content
            if (isDynamicComponent) {
                // Create a unique ID for dynamic elements
                const nodeId = this._generateStableNodeId(parent, textNode, true);
                parent.setAttribute('data-translate-id', nodeId);

                const originalText = this.originalEnTexts[nodeId] || rawText;
                if (!this.originalEnTexts[nodeId]) {
                    if (/^[a-zA-Z0-9\s.,!?\/()\-:%&#]+$/.test(rawText)) {
                        this.originalEnTexts[nodeId] = rawText;
                    }
                }

                if (
                    this.cachedTranslations[lang] &&
                    this.cachedTranslations[lang][nodeId]
                ) {
                    textNode.nodeValue = this.cachedTranslations[lang][nodeId];
                } else {
                    textsToTranslate.push({ id: nodeId, text: originalText });
                }
            } else {
                // Regular static content handling
                let nodeId = parent.getAttribute('data-translate-id');
                if (!nodeId) {
                    nodeId = this._generateStableNodeId(parent, textNode);
                    parent.setAttribute('data-translate-id', nodeId);
                }

                const originalText = this.originalEnTexts[nodeId] || rawText;
                if (!this.originalEnTexts[nodeId]) {
                    if (/^[a-zA-Z0-9\s.,!?\/()\-:%&#]+$/.test(rawText)) {
                        this.originalEnTexts[nodeId] = rawText;
                    }
                }

                if (
                    this.cachedTranslations[lang] &&
                    this.cachedTranslations[lang][nodeId]
                ) {
                    textNode.nodeValue = this.cachedTranslations[lang][nodeId];
                } else {
                    textsToTranslate.push({ id: nodeId, text: originalText });
                }
            }
        }

        return textsToTranslate;
    }

    /**
     * Restores the original text content and attributes of translatable elements
     * from a stored backup in localStorage or a predefined object.
     * 
     * This method handles both text node translations and attribute translations
     * for elements marked with specific data attributes. It also includes special
     * handling for `v-select` components to ensure placeholders are managed
     * correctly when tags are selected.
     * 
     * The method temporarily pauses any active observers to prevent infinite loops
     * during the restoration process and resumes them afterward.
     * 
     * @async
     * @returns {Promise<void>} Resolves when the restoration process is complete.
     * @throws {Error} Logs an error to the console if an issue occurs during the restoration process.
     */
    async restoreOriginalText() {
        // Temporarily pause the observer to prevent infinite loops
        const wasObserving = this.isObserving;
        if (wasObserving) {
            this.stopObserving();
        }

        try {
            const tempOriginalEnTexts = JSON.parse(localStorage.getItem('originalEnTexts')) || this.originalEnTexts;
            // Process all attribute translations
            this.translatableAttributes.forEach(attr => {
                document.querySelectorAll(`[data-translate-id-${attr}]`).forEach(el => {
                    const nodeId = el.getAttribute(`data-translate-id-${attr}`);

                    // Special handling for v-select placeholders
                    if (attr === 'placeholder') {
                        const vSelect = el.closest('.v-select');
                        if (vSelect) {
                            // Check if there are any selected tags
                            const hasSelectedTags = vSelect.querySelectorAll('.selected-tag').length > 0;

                            // If there are selected tags, the placeholder should be hidden
                            if (hasSelectedTags) {
                                el.removeAttribute('placeholder');
                                el.removeAttribute(`data-translate-id-${attr}`);
                                return;
                            }
                        }
                    }

                    if (tempOriginalEnTexts[nodeId]) {
                        el.setAttribute(attr, tempOriginalEnTexts[nodeId]);
                    }
                });
            });

            // Process all text node translations
            document.querySelectorAll('[data-translate-id]').forEach(el => {
                const nodeId = el.getAttribute('data-translate-id');
                if (tempOriginalEnTexts[nodeId]) {
                    // Find all direct text nodes
                    const textNodes = Array.from(el.childNodes)
                        .filter(node => node.nodeType === Node.TEXT_NODE);

                    if (textNodes.length > 0) {
                        // Update the first non-empty text node
                        for (const node of textNodes) {
                            if (node.nodeValue.trim()) {
                                node.nodeValue = tempOriginalEnTexts[nodeId];
                                break;
                            }
                        }
                    }
                }
            });

            // Final check: ensure that all v-select components with selected tags have no placeholders
            document.querySelectorAll('.v-select').forEach(vSelect => {
                if (vSelect.querySelectorAll('.selected-tag').length > 0) {
                    const input = vSelect.querySelector('input[placeholder]');
                    if (input) {
                        input.removeAttribute('placeholder');
                    }
                }
            });

        } catch (error) {
            console.error('Error restoring original text:', error);
        } finally {
            // Resume observer if it was active
            if (wasObserving) {
                setTimeout(() => this.startObserving(), 100);
            }
        }
    }

    /**
     * Generates a stable node ID for a given element and its text node.
     *
     * @param {HTMLElement} element - The parent HTML element containing the text node.
     * @param {Node|null} textNode - The text node for which the ID is being generated. Can be null.
     * @param {boolean} [flag=false] - A flag to determine if the ID should be marked as dynamic.
     * @returns {string} A stable node ID string.
     */
    _generateStableNodeId(element, textNode, flag = false) {
        const parentPath = this._getElementPath(element);
        const textIndex = textNode
            ? Array.from(element.childNodes)
                .filter(n => n.nodeType === Node.TEXT_NODE)
                .indexOf(textNode)
            : 0;

        // Check if the element is inside #sidebar-wrapper
        const isInSidebar = element.closest('#sidebar-wrapper');

        // Only add pageContext if not inside #sidebar-wrapper
        const pageContext = isInSidebar ? '' : (window.location.pathname || '');

        const base = `${pageContext}::${parentPath}::${textIndex}`;
        const prefix = flag ? 't-dynamic-' : 't-';
        return prefix + this._hashCode(base);
    }

    /**
     * Gets the CSS selector path for an element
     * This method generates a unique CSS selector path for a DOM element
     * that can be used to identify it consistently.
     * 
     * @private
     * @param {Element} el - DOM element
     * @returns {string} CSS selector path
     */
    _getElementPath(el) {
        const parts = [];
        while (el && el !== document.body) {
            const index = Array.from(el.parentNode.children).indexOf(el);
            parts.unshift(`${el.tagName.toLowerCase()}:nth-child(${index})`);
            el = el.parentNode;
        }
        return parts.join(' > ');
    }

    /**
     * Generates a hash code for a string
     * This method creates a consistent hash value for a given string input.
     * 
     * @private
     * @param {string} str - Input string
     * @returns {string} Hash code
     */
    _hashCode(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = (hash << 5) - hash + str.charCodeAt(i);
            hash |= 0;
        }
        return Math.abs(hash).toString(36);
    }

    /**
     * Translates texts and replaces them in the DOM
     * This method handles the actual translation process using the Google Translate API
     * and updates the DOM with translated content. It now ensures all original texts
     * are properly stored before translation.
     * 
     * @private
     * @param {Object[]} textsToTranslate - Array of text objects to translate
     * @param {string} lang - Target language code
     * @returns {Promise<void>}
     * @throws {Error} If translation API request fails
     */
    async _translateAndReplace(textsToTranslate, lang) {
        // Temporarily disconnect the observer to prevent infinite loops
        const wasObserving = this.isObserving;
        if (wasObserving) {
            this.stopObserving();
        }

        const maxChunkSize = 100;
        const chunks = [];
        for (let i = 0; i < textsToTranslate.length; i += maxChunkSize) {
            chunks.push(textsToTranslate.slice(i, i + maxChunkSize));
        }

        if (!this.cachedTranslations[lang]) {
            this.cachedTranslations[lang] = {};
        }

        // First, ensure all original texts are stored
        textsToTranslate.forEach(({ id, text }) => {
            if (!this.originalEnTexts[id] && text) {
                // Only store valid English text that matches our criteria
                if (/^[a-zA-Z0-9\s.,!?\/()\-:%&#]+$/.test(text)) {
                    this.originalEnTexts[id] = text;
                }
            }
        });

        // Save updated originalEnTexts to localStorage
        localStorage.setItem('originalEnTexts', JSON.stringify(this.originalEnTexts));

        try {
            for (let i = 0; i < chunks.length; i++) {
                const chunk = chunks[i];
                const texts = chunk.map(item => item.text);

                try {
                    const response = await fetch(`https://translation.googleapis.com/language/translate/v2?key=${this.apiKey}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            q: texts,
                            target: lang,
                            format: 'text',
                            source: 'en'
                        })
                    });

                    const data = await response.json();
                    if (!data.data || !data.data.translations) continue;

                    const translations = data.data.translations.map(t => t.translatedText);

                    chunk.forEach(({ id, attr, element, flag }, index) => {
                        const translatedText = translations[index];
                        this.cachedTranslations[lang][id] = translatedText;

                        if (attr && element) {
                            // Handle attribute translation
                            element.setAttribute(attr, translatedText);
                            if (flag && flag == 'remove') {
                                element.removeAttribute(attr);
                            }
                        } else {
                            // Handle text node translation
                            const el = document.querySelector(`[data-translate-id="${id}"]`);
                            if (el) {
                                const textNodes = Array.from(el.childNodes).filter(n => n.nodeType === Node.TEXT_NODE);
                                const originalText = this.originalEnTexts[id];

                                // First try to find the exact match
                                let node = textNodes.find(n => n.nodeValue.trim() === originalText);

                                // If exact match not found, find the first non-empty text node
                                if (!node) {
                                    node = textNodes.find(n => n.nodeValue.trim());
                                }

                                if (node) node.nodeValue = translatedText;
                            }
                        }
                    });

                } catch (err) {
                    console.error('Translation error:', err);
                    console.error('Failed chunk:', chunk);
                    // Continue with other chunks instead of throwing an error and stopping everything
                    continue;
                }
            }

            localStorage.setItem('translations', JSON.stringify(this.cachedTranslations));
        } finally {
            // Re-enable the observer only if it was active before
            if (wasObserving) {
                // Use a small delay to ensure all DOM updates have settled
                setTimeout(() => {
                    this.startObserving();
                }, 100);
            }
        }
    }

    /**
     * Gets the currently selected language
     * This method retrieves the user's selected language from localStorage
     * or returns 'en' as the default language.
     * 
     * @returns {string} Language code (defaults to 'en')
     */
    getSelectedLanguage() {
        const lang = localStorage.getItem('selectedLang');
        return lang ? lang : 'en';
    }
}


// WEBPACK FOOTER //
// ./src/mixins/translatorMixins.js